import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Autocomplete,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { reportsAPI, companiesAPI } from '../services/api';
import { usePermissions } from '../hooks/useAuth';
import { toast } from 'react-toastify';

interface Company {
  id: string;
  name: string;
  jurisdiction: string;
}

interface FinancialStatementStatusRow {
  companyId: string;
  companyName: string;
  jurisdiction: string;
  status: 'UPLOADED' | 'MISSING' | 'LATE';
  uploadedAt?: string | null;
  dueAt?: string | null;
  fiscalYearEndMonth?: number | null;
}

interface FinancialStatementStatusSummary {
  totalCompanies: number;
  returnedCompanies: number;
  uploaded: number;
  missing: number;
  late: number;
}

const currentYear = new Date().getFullYear();
const defaultStartDate = `${currentYear}-01-01`;
const defaultEndDate = `${currentYear}-12-31`;
const defaultReportingMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
const FISCAL_YEAR_END_MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatFiscalYearEndMonth = (fiscalYearEndMonth?: number | null): string => {
  if (
    typeof fiscalYearEndMonth === 'number' &&
    Number.isInteger(fiscalYearEndMonth) &&
    fiscalYearEndMonth >= 1 &&
    fiscalYearEndMonth <= 12
  ) {
    return FISCAL_YEAR_END_MONTH_SHORT[fiscalYearEndMonth - 1];
  }
  return 'Dec (default)';
};

const formatIsoDateUtc = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toISOString().slice(0, 10);
};

const Reports: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFailedAction, setLastFailedAction] = useState<(() => void) | null>(null);

  // Report parameters — sensible defaults (current year for dates)
  const [format, setFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [jurisdiction, setJurisdiction] = useState('');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [financialPeriodType, setFinancialPeriodType] = useState<'MONTH' | 'FISCAL_YEAR'>('MONTH');
  const [financialReportingMonth, setFinancialReportingMonth] = useState(defaultReportingMonth);
  const [financialFiscalYear, setFinancialFiscalYear] = useState(currentYear);
  const [missingOnly, setMissingOnly] = useState(false);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialError, setFinancialError] = useState<string | null>(null);
  const [financialStatuses, setFinancialStatuses] = useState<FinancialStatementStatusRow[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialStatementStatusSummary>({
    totalCompanies: 0,
    returnedCompanies: 0,
    uploaded: 0,
    missing: 0,
    late: 0,
  });
  const [financialDueAt, setFinancialDueAt] = useState<string | null>(null);

  // Load companies for autocomplete
  React.useEffect(() => {
    loadCompanies();
  }, []);

  React.useEffect(() => {
    fetchFinancialStatementStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialPeriodType, financialReportingMonth, financialFiscalYear, missingOnly]);

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await companiesAPI.getAll({ limit: 1000 });
      setCompanies(response.data.data || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleDownload = async (
    downloadFn: (params: any) => Promise<{ data: BlobPart }>,
    params: any,
    reportName: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      setLastFailedAction(null);

      const response = await downloadFn(params);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : format;
      const filename = `${reportName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.${ext}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${reportName} downloaded (${filename})`);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || `Report failed: ${reportName}`;
      setError(message);
      setLastFailedAction(() => () => handleDownload(downloadFn, params, reportName)); // retry = call same again
      console.error(`Error downloading ${reportName}:`, err);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialStatementStatuses = async () => {
    try {
      setFinancialLoading(true);
      setFinancialError(null);
      const response = await companiesAPI.getFinancialStatementStatuses({
        periodType: financialPeriodType,
        reportingMonth: financialPeriodType === 'MONTH' ? financialReportingMonth : undefined,
        fiscalYear: financialPeriodType === 'FISCAL_YEAR' ? financialFiscalYear : undefined,
        missingOnly,
      });
      setFinancialStatuses(response.data?.rows || []);
      setFinancialSummary(
        response.data?.summary || {
          totalCompanies: 0,
          returnedCompanies: 0,
          uploaded: 0,
          missing: 0,
          late: 0,
        },
      );
      setFinancialDueAt(response.data?.period?.dueAt || null);
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.message ||
        'Failed to load financial statement period statuses';
      setFinancialError(message);
      setFinancialStatuses([]);
      setFinancialSummary({
        totalCompanies: 0,
        returnedCompanies: 0,
        uploaded: 0,
        missing: 0,
        late: 0,
      });
      setFinancialDueAt(null);
    } finally {
      setFinancialLoading(false);
    }
  };

  const getStatusChip = (status: FinancialStatementStatusRow['status']) => {
    if (status === 'UPLOADED') {
      return <Chip size="small" label="Uploaded" color="success" />;
    }
    if (status === 'LATE') {
      return <Chip size="small" label="Late" color="warning" />;
    }
    return <Chip size="small" label="Missing" variant="outlined" />;
  };

  const reportCategories = [
    {
      title: 'Ownership Reports',
      icon: <AccountTreeIcon />,
      color: '#1976d2',
      reports: [
        {
          name: 'Ownership Hierarchy',
          description: 'Multi-level ownership tree showing ownership structures',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadOwnershipHierarchy,
            { format, companyId: selectedCompany?.id, jurisdictions: jurisdiction ? [jurisdiction] : undefined },
            'Ownership Hierarchy'
          ),
        },
        {
          name: 'Beneficial Ownership',
          description: 'Ultimate beneficial owners with percentage calculations',
          requiresCompany: true,
          action: () => {
            if (!selectedCompany) {
              setError('Please select a company');
              return;
            }
            handleDownload(
              reportsAPI.downloadBeneficialOwnership,
              { format, companyId: selectedCompany.id },
              'Beneficial Ownership'
            );
          },
        },
        {
          name: 'Shareholder Register',
          description: 'Statutory shareholder register for a company',
          requiresCompany: true,
          action: () => {
            if (!selectedCompany) {
              setError('Please select a company');
              return;
            }
            handleDownload(
              reportsAPI.downloadShareholderRegister,
              { format, companyId: selectedCompany.id },
              'Shareholder Register'
            );
          },
        },
        {
          name: 'Cross-Holdings',
          description: 'Detect circular and mutual ownership structures',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadCrossHoldings,
            { format, jurisdictions: jurisdiction ? [jurisdiction] : undefined },
            'Cross-Holdings'
          ),
        },
      ],
    },
    {
      title: 'Compliance Reports',
      icon: <SecurityIcon />,
      color: '#2e7d32',
      reports: [
        {
          name: 'Director Register',
          description: 'List of directors with appointment dates and details',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadDirectorRegister,
            { format, companyId: selectedCompany?.id, jurisdiction },
            'Director Register'
          ),
        },
        {
          name: 'Officer List',
          description: 'All officers (directors and shareholders) by jurisdiction',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadOfficerList,
            { format, jurisdiction },
            'Officer List'
          ),
        },
        {
          name: 'Compliance Status',
          description: 'Data quality issues and compliance gaps',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadComplianceStatus,
            { format, jurisdiction },
            'Compliance Status'
          ),
        },
        {
          name: 'Changes Report',
          description: 'Audit trail of changes within a date range',
          requiresCompany: false,
          requiresDates: true,
          action: () => {
            if (!startDate || !endDate) {
              setError('Please select start and end dates');
              return;
            }
            handleDownload(
              reportsAPI.downloadChangesReport,
              { format, startDate, endDate },
              'Changes Report'
            );
          },
        },
      ],
    },
    {
      title: 'Financial Reports',
      icon: <AttachMoneyIcon />,
      color: '#ed6c02',
      reports: [
        {
          name: 'Capital Structure',
          description: 'Authorized and issued capital breakdown by company',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadCapitalStructure,
            { format, companyId: selectedCompany?.id, jurisdiction },
            'Capital Structure'
          ),
        },
        {
          name: 'Share Capital Analysis',
          description: 'Total share capital analysis by jurisdiction',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadShareCapitalAnalysis,
            { format, jurisdiction },
            'Share Capital Analysis'
          ),
        },
        {
          name: 'Valuation Summary',
          description: 'Portfolio valuation summary (estimated from share capital)',
          requiresCompany: false,
          action: () => handleDownload(
            reportsAPI.downloadValuationSummary,
            { format },
            'Valuation Summary'
          ),
        },
      ],
    },
  ];

  const getReportDisabled = (report: { requiresCompany?: boolean; requiresDates?: boolean }) =>
    loading ||
    (!!report.requiresCompany && !selectedCompany) ||
    (!!report.requiresDates && (!startDate || !endDate));

  const getReportHint = (report: { requiresCompany?: boolean; requiresDates?: boolean }) => {
    if (report.requiresCompany && !selectedCompany) return 'Select a company above';
    if (report.requiresDates && (!startDate || !endDate)) return 'Select date range above';
    return null;
  };

  const downloadButtonLabel = loading ? 'Generating report…' : `Download ${format.toUpperCase()}`;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" color="inherit">Dashboard</Link>
        <Typography color="text.primary">Reports</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          Reports & Export
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate and download comprehensive reports in PDF, Excel, or CSV format
        </Typography>
      </Box>

      {/* Error with optional Retry */}
      {error && (
        <Alert
          severity="error"
          onClose={() => { setError(null); setLastFailedAction(null); }}
          action={
            lastFailedAction ? (
              <Button color="inherit" size="small" onClick={() => { setError(null); lastFailedAction?.(); }}>
                Retry
              </Button>
            ) : null
          }
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Filter Panel */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Report Parameters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Set format and optional company, jurisdiction, or date range. Some reports require a company or dates.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={format}
                label="Export Format"
                onChange={(e) => setFormat(e.target.value as any)}
              >
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="excel">Excel (XLSX)</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              loading={companiesLoading}
              value={selectedCompany}
              onChange={(_, value) => setSelectedCompany(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company (Optional)"
                  placeholder="Select a company..."
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Jurisdiction (Optional)"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="e.g., BVI, UAE"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="e.g. current year"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        {selectedCompany && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={`Selected: ${selectedCompany.name} (${selectedCompany.jurisdiction})`}
              onDelete={() => setSelectedCompany(null)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Financial Statement Upload Status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Holdings-wide Company × Period status matrix derived from FINANCIAL_STATEMENT documents.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel>Period Type</InputLabel>
              <Select
                label="Period Type"
                value={financialPeriodType}
                onChange={(event) => setFinancialPeriodType(event.target.value as 'MONTH' | 'FISCAL_YEAR')}
              >
                <MenuItem value="MONTH">Calendar Month</MenuItem>
                <MenuItem value="FISCAL_YEAR">Fiscal Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            {financialPeriodType === 'MONTH' ? (
              <TextField
                fullWidth
                label="Reporting Month"
                type="month"
                value={financialReportingMonth}
                onChange={(event) => setFinancialReportingMonth(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <TextField
                fullWidth
                label="Fiscal Year"
                type="number"
                value={financialFiscalYear}
                onChange={(event) => {
                  const nextFiscalYear = Number(event.target.value);
                  if (Number.isNaN(nextFiscalYear)) return;
                  setFinancialFiscalYear(nextFiscalYear);
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={3} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={missingOnly}
                  onChange={(event) => setMissingOnly(event.target.checked)}
                />
              }
              label="Missing only"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
              <Chip size="small" color="success" label={`Uploaded: ${financialSummary.uploaded}`} />
              <Chip size="small" variant="outlined" label={`Missing: ${financialSummary.missing}`} />
              <Chip size="small" color="warning" label={`Late: ${financialSummary.late}`} />
            </Stack>
          </Grid>
        </Grid>

        {financialDueAt && financialPeriodType === 'MONTH' && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Period due date: {formatIsoDateUtc(financialDueAt)}
          </Typography>
        )}
        {financialPeriodType === 'FISCAL_YEAR' && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Due dates are based on each company&apos;s Year End month.
          </Typography>
        )}

        {financialError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {financialError}
          </Alert>
        )}

        {financialLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : financialStatuses.length === 0 ? (
          <Alert severity="info">
            {missingOnly
              ? 'No missing/late companies for this period.'
              : 'No companies found for the selected period.'}
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Financial statement status matrix">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Jurisdiction</TableCell>
                  <TableCell>Year End</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uploaded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financialStatuses.map((row) => (
                  <TableRow key={row.companyId} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/companies/${encodeURIComponent(row.companyId)}`}>
                        {row.companyName}
                      </Link>
                    </TableCell>
                    <TableCell>{row.jurisdiction || '—'}</TableCell>
                    <TableCell>
                      {financialPeriodType === 'FISCAL_YEAR'
                        ? formatFiscalYearEndMonth(row.fiscalYearEndMonth)
                        : '—'}
                    </TableCell>
                    <TableCell>{formatIsoDateUtc(row.dueAt)}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell>
                      {row.uploadedAt ? new Date(row.uploadedAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Report Categories */}
      {reportCategories.map((category) => (
        <Box key={category.title} sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {category.icon}
            {category.title}
          </Typography>
          <Grid container spacing={3}>
            {category.reports.map((report) => (
              <Grid item xs={12} md={6} lg={4} key={report.name}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {report.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                    {report.requiresCompany && (
                      <Chip
                        label="Requires Company"
                        size="small"
                        color="warning"
                        sx={{ mt: 1 }}
                      />
                    )}
                    {'requiresDates' in report && report.requiresDates && (
                      <Chip
                        label="Requires Dates"
                        size="small"
                        color="info"
                        sx={{ mt: 1 }}
                      />
                    )}
                    {getReportHint(report) && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        {getReportHint(report)}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    {hasPermission('reports:export') ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                        disabled={getReportDisabled(report)}
                        onClick={report.action}
                        sx={{ backgroundColor: category.color }}
                        aria-label={getReportDisabled(report) ? getReportHint(report) ?? undefined : `Download ${report.name}`}
                      >
                        {downloadButtonLabel}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{ opacity: 0.5 }}
                      >
                        No Permission
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Container>
  );
};

export default Reports;
